import { useCallback, useEffect, useState } from "react";
import { Loader2, Search, X, Download, Eye, FileDown, Inbox } from "lucide-react";
import { toast } from "sonner";
import { listData, updateData, downloadFile } from "../adminApi";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

const StatusBadge = ({ value }) => (
  <span className="inline-block rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">{value || "New"}</span>
);

export default function DataResource({ resource, title, columns, filters = [], isApplications = false }) {
  const [data, setData] = useState({ items: [], total: 0, statuses: [] });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [filterVals, setFilterVals] = useState({});
  const [active, setActive] = useState(null); // detail record
  const [saving, setSaving] = useState(false);
  const limit = 15;

  const buildParams = useCallback(() => {
    const p = { page, limit };
    if (search.trim()) p.search = search.trim();
    Object.entries(filterVals).forEach(([k, v]) => { if (v) p[k] = v; });
    return p;
  }, [page, search, filterVals]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await listData(resource, buildParams()));
    } catch {
      toast.error("Failed to load data.");
    } finally {
      setLoading(false);
    }
  }, [resource, buildParams]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [resource]);

  const applySearch = (e) => { e.preventDefault(); setPage(1); load(); };
  const setFilter = (k, v) => { setFilterVals((f) => ({ ...f, [k]: v })); setPage(1); };
  const clearFilters = () => { setFilterVals({}); setSearch(""); setPage(1); };

  const exportFile = async (ext) => {
    const qs = new URLSearchParams(Object.fromEntries(Object.entries(buildParams()).filter(([k]) => !["page", "limit"].includes(k)))).toString();
    try {
      await downloadFile(`/export/${resource}.${ext}?${qs}`, `${resource}.${ext}`);
      toast.success(`Exported ${ext.toUpperCase()}`);
    } catch { toast.error("Export failed."); }
  };

  const totalPages = Math.max(1, Math.ceil(data.total / limit));

  return (
    <div data-testid={`admin-${resource}`}>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{title}</h1>
          <p className="text-sm text-slate-500">{data.total} total record{data.total === 1 ? "" : "s"}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => exportFile("csv")} data-testid={`${resource}-export-csv`} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
            <FileDown className="h-4 w-4" /> CSV
          </button>
          <button onClick={() => exportFile("xlsx")} data-testid={`${resource}-export-xlsx`} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
            <Download className="h-4 w-4" /> Excel
          </button>
        </div>
      </div>

      {/* Search + filters */}
      <div className="mb-5 rounded-xl border border-slate-200 bg-white p-4">
        <form onSubmit={applySearch} className="mb-3 flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…" data-testid={`${resource}-search`}
              className="w-full rounded-lg border border-slate-300 py-2 pl-9 pr-3 text-sm outline-none focus:border-orange" />
          </div>
          <button type="submit" className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white">Search</button>
        </form>
        {filters.length > 0 && (
          <div className="flex flex-wrap items-end gap-3">
            {filters.map((f) => (
              <div key={f.key} className="flex flex-col gap-1">
                <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{f.label}</label>
                {f.type === "select" ? (
                  <select value={filterVals[f.key] || ""} onChange={(e) => setFilter(f.key, e.target.value)} data-testid={`${resource}-filter-${f.key}`}
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-orange">
                    <option value="">All</option>
                    {(f.options || data.statuses).map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <input type={f.type || "text"} value={filterVals[f.key] || ""} onChange={(e) => setFilter(f.key, e.target.value)} data-testid={`${resource}-filter-${f.key}`}
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-orange" placeholder={f.label} />
                )}
              </div>
            ))}
            <button onClick={clearFilters} className="inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm text-slate-500 hover:text-slate-800">
              <X className="h-4 w-4" /> Clear
            </button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-orange" /></div>
        ) : data.items.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-20 text-slate-400" data-testid={`${resource}-empty`}>
            <Inbox className="h-10 w-10" /> <p className="text-sm">No records found.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  {columns.map((c) => <th key={c.key} className="px-4 py-3 font-semibold">{c.label}</th>)}
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold">Date</th>
                  <th className="px-4 py-3 text-right font-semibold">Action</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((row) => (
                  <tr key={row.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`${resource}-row`}>
                    {columns.map((c) => <td key={c.key} className="px-4 py-3 text-slate-700">{row[c.key] || "—"}</td>)}
                    <td className="px-4 py-3"><StatusBadge value={row.status} /></td>
                    <td className="px-4 py-3 text-slate-500">{fmtDate(row.createdAt)}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => setActive(row)} data-testid={`${resource}-view`} className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100">
                        <Eye className="h-3.5 w-3.5" /> View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {data.total > limit && (
        <div className="mt-4 flex items-center justify-between text-sm text-slate-600">
          <span>Page {page} of {totalPages}</span>
          <div className="flex gap-2">
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="rounded-lg border border-slate-300 px-3 py-1.5 disabled:opacity-40">Prev</button>
            <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="rounded-lg border border-slate-300 px-3 py-1.5 disabled:opacity-40">Next</button>
          </div>
        </div>
      )}

      {active && (
        <DetailModal
          resource={resource} record={active} statuses={data.statuses} isApplications={isApplications}
          saving={saving} setSaving={setSaving}
          onClose={() => setActive(null)}
          onSaved={() => { setActive(null); load(); }}
        />
      )}
    </div>
  );
}

const HIDE = ["id", "status", "adminNotes", "followUp", "createdAt", "updatedAt", "consent", "source"];

function DetailModal({ resource, record, statuses, isApplications, saving, setSaving, onClose, onSaved }) {
  const [status, setStatus] = useState(record.status || statuses[0] || "New");
  const [notes, setNotes] = useState(record.adminNotes || "");
  const [followUp, setFollowUp] = useState(record.followUp || "");

  const save = async () => {
    setSaving(true);
    try {
      await updateData(resource, record.id, { status, adminNotes: notes, followUp });
      toast.success("Updated successfully.");
      onSaved();
    } catch (e) {
      toast.error(e.message || "Update failed.");
    } finally { setSaving(false); }
  };

  const dl = async () => {
    try {
      await downloadFile(`/applications/${record.id}/resume`, record.resume?.originalName || "resume");
    } catch { toast.error("No resume available."); }
  };

  const entries = Object.entries(record).filter(([k, v]) => !HIDE.includes(k) && typeof v !== "object");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6" onClick={(e) => e.stopPropagation()} data-testid="admin-detail-modal">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-bold text-slate-900">Record details</h2>
          <button onClick={onClose} className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100"><X className="h-5 w-5" /></button>
        </div>
        <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
          {entries.map(([k, v]) => (
            <div key={k} className="rounded-lg bg-slate-50 px-3 py-2">
              <div className="text-[11px] uppercase tracking-wide text-slate-400">{k}</div>
              <div className="break-words text-sm text-slate-800">{String(v || "—")}</div>
            </div>
          ))}
        </div>

        {isApplications && record.resume && (
          <button onClick={dl} data-testid="admin-download-resume" className="mb-5 inline-flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
            <Download className="h-4 w-4" /> Download Resume ({record.resume.originalName})
          </button>
        )}

        <div className="flex flex-col gap-4 border-t border-slate-200 pt-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <label className="flex flex-col gap-1.5 text-sm text-slate-600">
              Status
              <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="admin-status-select" className="rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-orange">
                {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-1.5 text-sm text-slate-600">
              Assign follow-up
              <input value={followUp} onChange={(e) => setFollowUp(e.target.value)} data-testid="admin-followup" placeholder="e.g. Call by 30 Jul / Assigned to…" className="rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-orange" />
            </label>
          </div>
          <label className="flex flex-col gap-1.5 text-sm text-slate-600">
            Internal notes
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="admin-notes" rows={3} className="rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-orange" placeholder="Add internal notes…" />
          </label>
          <button onClick={save} disabled={saving} data-testid="admin-save-record" className="inline-flex items-center justify-center gap-2 rounded-lg bg-orange px-5 py-2.5 font-medium text-white hover:opacity-90 disabled:opacity-60">
            {saving ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</> : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}
